import * as CONSTANTS from '../constants/actions/filters';
import { formatCheckboxes, formatRange, formatText } from '../utilities/parameters';
import { updateHash, parametersToHash, hashToParameters, findHashValue } from '../utilities/hash';
import { determineFilters, extractPSTIdFromURL, mapOrder } from '../utilities/filters';
import * as HASH from '../constants/config/hash';
import { TOOLS } from '../constants/config/parameters';
import { LEFT, RIGHT } from '../constants/config/table';
import { PARAMETERS_ALL, PARAMETERS_DEFAULT } from '../constants/config/filters';

/** 
 * @name INITIAL_STATE
 * @constant 
 * @description
 * @type {Object}
 * @default
*/
const INITIAL_STATE = {
    comparing: false,
    allParameters: [],
    lastChangeOrder: Date().toString(),
    parameters: [],
    modifiedParameters: [],
    prioritizeDescription: false,
    prioritizeHardware: false,
    prioritizeStatus: false,
    prioritizeTools: false,
    sortedParameters: [],
    tooltip: {},
    sortPriority: [],
    buttonDisabled: true,
    originalDefaultParams: [],
    modifiedFiltersAry: [],
    resetSignal: false,
    quickFilter: {},
};

function updateParameters( parameters, defaultParameterIds ){
    const selectedIds = parameters.map(p => p.id),
        isSameLength = selectedIds.length === defaultParameterIds.length,
        hasAllDefaultIds = selectedIds.every(id => defaultParameterIds.includes(id)),
        isSameAsDefault = isSameLength && hasAllDefaultIds,
        hash = parametersToHash(selectedIds);
    
    if( isSameAsDefault ){
        updateHash( HASH.PARAMETERS_DISPLAY, null );
    } else {
        updateHash( HASH.PARAMETERS_DISPLAY, hash );
    }
}

/**
 * **DO NOT** Manipulate the state directly, always apply to a new object first!! If you don't redux will not 
 * recognize a change in the data and the components will not receive the correct information.
 * 
 * @name reducer
 * @function
 * @description Handles reduction of data accross components for the piece of the site implied by the filename
 * @param {Object} state Current state of the data
 * @param {Object} action Information passed from the corresponding actions file
 */
const reducer = ( state = INITIAL_STATE, action ) => {

    var parameters = [ ...state.parameters ];

    /** 
     * Meta example case:
     *      case CONSTANTS[ whateverTheActionIs ]:
     *          return { ...state, key: action.payload.key }
     */
	switch( action.type ){

        case CONSTANTS.APPLY_HASH_CHANGE:
            let modFilters = determineFilters( parameters )
            modFilters.modified.forEach(function(value) {
                    state.modifiedFiltersAry.push(value);
              });
            // mark all modified parameters as active 
            parameters = parameters.map( parameter => {
                parameter.active = action.payload.modified.includes( parameter.id );
                return parameter;
            });

            return { ...state, parameters, hashTime: new Date() };

        case CONSTANTS.APPLY_PARAMETERS:
            if(action.payload.parameters){
                parameters = [ ...state.allParameters.filter(({ id }) => action.payload.parameters.includes( id ) ) ];
            }
            parameters = mapOrder( parameters, action.payload.parameters, 'id' );   
             updateParameters( parameters, state.originalDefaultParams );
            return { ...state, parameters, lastChangeOrder: Date().toString() };

        case CONSTANTS.HIDE_PARAMETER:
            let hideIndex = parameters.findIndex( parameter => parameter.id === action.payload.parameter );
            parameters.splice( hideIndex, 1 );
            updateParameters( parameters, state.originalDefaultParams );
            return { ...state, parameters, buttonDisabled: false };

        case CONSTANTS.REORDER:
            
            let subjectIndex = parameters.findIndex( parameter => parameter.id === action.payload.subject ),
            subject = parameters.splice( subjectIndex, 1 ),
            targetIndex = parameters.findIndex( parameter => parameter.id === action.payload.target );

            // simple add first item
            if( targetIndex === 0 && action.payload.side === RIGHT ){
                parameters.unshift( ...subject );

            // simple add last item
            } else if( targetIndex === ( parameters.length - 1 ) && action.payload.side === LEFT ){
                parameters.push( ...subject );

            // every other option
            } else {

                
                if( action.payload.side === LEFT ) targetIndex += 1; 
                
                parameters.splice( targetIndex, 0, ...subject );

            }
            
            return { ...state, parameters };

        case CONSTANTS.RESET_SORT:
            return { ...state, newSort: action.payload.parameter };

        case CONSTANTS.RESET_PARAMETERS:
            let activeParameters = state.allParameters.map( parameter => {
                parameter.active = false;
                return parameter;
                });
            parameters = activeParameters.filter( parameter => parameter.default );
            return { ...state, parameters, showAll: PARAMETERS_DEFAULT, lastReset: new Date(), allParameters: activeParameters };
        
        case CONSTANTS.SHOW_ALL:
            return { ...state, parameters: [ ...state.allParameters ], showAll: PARAMETERS_ALL };

            case CONSTANTS.SET_PARAMETERS:
                let excludeTools = true;
                let ignoredBlanks = [];
                let returnedParameters = action.payload.parameters.filter((parameter) => action.payload.details[parameter.field]);
                let allParameters = returnedParameters.map((parameter) => {
                  let fParameter = null;
                  if (parameter.field === TOOLS) excludeTools = false;
                  switch (action.payload.details[parameter.field].filterType) {
                    case 'Checkboxes':
                      fParameter = formatCheckboxes(parameter, action.payload.details[parameter.field]);
                      break;
                    case 'Sliders':
                    // case for cce inviornment.
                    case 'Slider':
                      fParameter = formatRange(parameter, action.payload.details[parameter.field], action.payload.values[parameter.field] || []);
                      break;
                    default:
                      fParameter = formatText(parameter, action.payload.details[parameter.field]);
                      break;
                  }
                  if (fParameter.allowBlanks) ignoredBlanks.push(fParameter.id);
                  return fParameter;
                });
                const originalDefaultParams = allParameters
                .filter((parameter) => parameter.default)
                .map((parameter) => parameter.id);
              
                let hash = findHashValue(HASH.PARAMETERS_DISPLAY);
                let presetParams = hash ? hashToParameters(hash) : null;
                const pstID = extractPSTIdFromURL();
              
                // Retrieve the defaultParams from local storage and split it into an array
                let defaultParams = localStorage.getItem(`defaultParams${pstID}`);
                if (defaultParams) {
                  defaultParams = defaultParams.split(',').map((id) => id.trim());
                } else {
                  defaultParams = originalDefaultParams;
                }
              
                // Set the 'default' property of each parameter based on whether it's in defaultParams
                allParameters.forEach((parameter) => {
                  parameter.default = defaultParams.includes(parameter.id);
                });
              
                let visibleParameters = allParameters.filter((parameter) => {
                  if (presetParams) return presetParams.includes(parameter.id);
                  return parameter.default;
                });
              
                return { ...state, allParameters, parameters: visibleParameters, classifications: action.payload.classifications, excludeTools, ignoredBlanks, originalDefaultParams };
              
            case CONSTANTS.UPDATE_DEFAULT_PARAMETERS:
                const newDefaultValue = action.payload.newDefaultValue;
                const storedSelections = action.payload.parameterIds;

              
                // Create a new copy of the allParameters array with updated default values
                const updatedAllParameters = state.allParameters.map((parameter) => {
                  if (storedSelections? storedSelections.includes(parameter.id): '' ) {
                    // Update the default property for selected parameters
                    return {
                      ...parameter,
                      default: newDefaultValue,
                    };
                  } else {
                    // For parameters that don't match, set default to false
                    return {
                      ...parameter,
                      default: false,
                    };
                  }
                });
                return {
                  ...state,
                  allParameters: updatedAllParameters,
                };
        case CONSTANTS.SET_QUICK_FILTER:
            return { ...state, quickFilter: action.payload.html };

        case CONSTANTS.SET_TOOLTIP:
            return { ...state, tooltip: action.payload }

        case CONSTANTS.PRIORITIZE_PARAMETER:

            const find = parameter => parameter.id === action.payload.parameter;

            let index = parameters.findIndex( find ),
            parameter = ( index > -1 ) ? parameters.splice( index, 1 )[ 0 ] : state.allParameters.find( find );

            if( action.payload.show && parameter ) parameters.unshift( parameter );

            updateParameters( parameters, state.originalDefaultParams );

            return { ...state, parameters, buttonDisabled: false };

        case CONSTANTS.SORT_BY_COLUMN:
            let column = action.payload.column,
            order = action.payload.order,
            addToCurrentSort = action.payload.addToCurrentSort,
            currentPriority = action.payload.currentPriority,
            sortPriority = [];
            if( order ) {
                let exists = currentPriority.find( sort => sort.column === column );
                if( !exists ) currentPriority.push({ column, priority: currentPriority.length + 1 });
                sortPriority = !addToCurrentSort ? [{ column, priority: 1 }] : currentPriority;
            }
            else {
                if( currentPriority && currentPriority.length > 0 ) {
                    let columnIndex = currentPriority.findIndex( sort => sort.column === column );
                    if( columnIndex > -1  ) {
                        currentPriority.splice( columnIndex, 1 );
                        currentPriority.map((sort, index) => sort.priority = index + 1 );
                        sortPriority = currentPriority;
                    }
                }
            }
            return { ...state, sortPriority };   
            case CONSTANTS.RESET_COLUMN:
                let resetted=false;
                state.modifiedFiltersAry.forEach((filter)=>{
                    if(filter===action.payload.parameter){
                        resetted = action.payload.value
                    }
                })
              return { ...state, resetSignal: resetted };

		default:
			return state;
    }
    
}

export default reducer;